<template>
    <div>
        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="name"
                name="name"
                :placeholder="$t('fields.last_name_and_first_name')"
                :value="name"
                autocomplete="off"
                @input="
                    $emit('update:name', $event.target.value);
                    $v.name.$touch();
                "
            />
        </div>

        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="address1"
                name="address1"
                :placeholder="$t('fields.address1')"
                :value="address1"
                autocomplete="off"
                @input="
                    $emit('update:address1', $event.target.value);
                    $v.address1.$touch();
                "
            />

            <div class="form__help">{{ $t('fields.address1_help') }}</div>

            <div v-if="$v.address1.$dirty">
                <p class="form__error" v-if="!$v.address1.required">
                    {{ $t('fields.street_e_required') }}
                </p>
            </div>
        </div>

        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="address2"
                name="address2"
                :placeholder="$t('fields.address2')"
                :value="address2"
                autocomplete="off"
                @input="
                    $emit('update:address2', $event.target.value);
                    $v.address2.$touch();
                "
            />

            <div class="form__help">{{ $t('fields.address2_help') }}</div>
        </div>

        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="postcode"
                name="postcode"
                :placeholder="$t('fields.postcode')"
                :value="postcode"
                autocomplete="off"
                @input="
                    $emit('update:postcode', $event.target.value);
                    $v.postcode.$touch();
                "
            />

            <div v-if="$v.postcode.$dirty">
                <p class="form__error" v-if="!$v.postcode.required">
                    {{ $t('fields.postcode_e_required') }}
                </p>
            </div>
        </div>

        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="city"
                name="city"
                :placeholder="$t('fields.city')"
                :value="city"
                autocomplete="off"
                @input="
                    $emit('update:city', $event.target.value);
                    $v.city.$touch();
                "
            />

            <div v-if="$v.city.$dirty">
                <p class="form__error" v-if="!$v.city.required">
                    {{ $t('fields.city_e_required') }}
                </p>
            </div>
        </div>

        <div class="row form__row">
            <input
                type="text"
                class="form__field"
                id="region"
                name="region"
                :placeholder="$t('fields.region')"
                :value="region"
                autocomplete="off"
                @input="
                    $emit('update:region', $event.target.value);
                    $v.region.$touch();
                "
            />

            <div class="form__help">{{ $t('fields.region_help') }}</div>
        </div>

        <div class="row form__row">
            <label class="form__label" for="country">{{ $t('fields.country') }}</label>
            <country-select
                class="form__field"
                id="country"
                name="country"
                :preselectedValue="country"
                autocomplete="off"
                @input="
                    $emit('update:country', $event);
                    $v.country.$touch();
                    clearRemoteError('country');
                "
            />

            <div v-if="$v.country.$dirty">
                <p class="form__error" v-if="!$v.country.remote">
                    {{ remoteErrors.country[0] }}
                </p>
            </div>
        </div>

        <!-- <div class="row form__row">
            <label class="form__label" for="system_status">{{ $t('fields.system_status') }}</label>
            <input
                type="text"
                class="form__field"
                id="system_status"
                name="system_status"
                :placeholder="$t('fields.system_status')"
                :value="system_status"
                autocomplete="off"
                @input="
                    $emit('update:system_status', $event.target.value);
                    $v.system_status.$touch();
                "
            />
        </div> -->

        <div class="row form__row">
            <label class="form__label" for="boiler">{{ $t('fields.boiler') }}</label>
            <select
                class="form__field"
                id="boiler"
                name="boiler"
                :placeholder="$t('fields.boiler')"
                :value="boiler_image_name"
                autocomplete="off"
                @input="
                    $emit('update:boiler_image_name', $event.target.value);
                    $v.boiler_image_name.$touch();
                "
            >
                <option v-for="(label, key) in boilerOptions" :key="key" :value="key">
                    {{ label }}
                </option>
            </select>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import remote from '@/validators/remote';
import CountrySelect from '@/components/CountrySelect';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'system-form-base',
    components: {
        CountrySelect,
    },
    props: {
        name: {
            type: String,
        },
        address1: {
            type: String,
        },
        address2: {
            type: String,
        },
        postcode: {
            type: String,
        },
        city: {
            type: String,
        },
        region: {
            type: String,
        },
        country: {
            type: String,
        },
        system_status: {
            type: String,
        },
        boiler_image_name: {
            type: String,
        },
        boiler_name: {
            type: String,
        },
        remoteErrors: {
            type: Object,
        },
        availableBoilers: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {};
    },
    validations() {
        return {
            name: {},
            address1: {
                required,
            },
            address2: {},
            postcode: {
                required,
            },
            city: {
                required,
            },
            region: {},
            country: {
                remote: remote(this.remoteErrors, 'country'),
            },
            system_status: {},
            boiler_image_name: {},
        };
    },
    computed: {
        boilerOptions() {
            const boilers = {...this.availableBoilers}
            if(!this.availableBoilers[this.boiler_image_name]) {
                boilers[this.boiler_image_name] = this.boiler_name
            }
            return boilers
        }
    },
    methods: {
        clearRemoteError(key) {
            if (this.remoteErrors && this.remoteErrors[key]) {
                let clone = cloneDeep(this.remoteErrors);

                clone[key] = null;

                this.$emit('update:remote-errors', clone);
            }
        },
    },
};
</script>
